import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Snackbar,
	Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RichTextEditor from "./RichTextEditor";
import { formatServiceDate, stripHtml } from "../../utils/serviceWindow";

const VisitTypeMessageModal = ({
	open,
	onClose,
	visitType,
	message,
	isAdmin,
	onSave,
	saving = false,
}) => {
	const [editMode, setEditMode] = useState(false);
	const [draftHtml, setDraftHtml] = useState("");
	const [copyNotice, setCopyNotice] = useState("");

	useEffect(() => {
		if (open) {
			setDraftHtml(message?.message_html || "");
			setEditMode(false);
			setCopyNotice("");
		}
	}, [open, message?.message_html, visitType?.id]);

	if (!visitType) return null;

	const handleCopy = async () => {
		const plainText = stripHtml(message?.message_html || "");
		if (!plainText.trim()) {
			setCopyNotice("No message to copy yet.");
			return;
		}

		try {
			await navigator.clipboard.writeText(plainText);
			setCopyNotice("Message copied to clipboard.");
		} catch (error) {
			console.error("Copy failed:", error);
			setCopyNotice("Unable to copy message.");
		}
	};

	const handleSave = async () => {
		await onSave?.(visitType.id, draftHtml, message?.id);
		setEditMode(false);
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
				<DialogTitle>
					{visitType.name}
					<Typography variant="body2" color="text.secondary">
						{formatServiceDate(visitType.start_date)} –{" "}
						{formatServiceDate(visitType.end_date)}
					</Typography>
				</DialogTitle>
				<DialogContent dividers>
					{isAdmin && !editMode && (
						<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
							<Button size="small" onClick={() => setEditMode(true)}>
								{message?.message_html ? "Edit template" : "Add template"}
							</Button>
						</Box>
					)}

					{isAdmin && editMode ? (
						<>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Message template
							</Typography>
							<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
								Only one active template is kept per visit type. Saving will replace
								the current active message.
							</Typography>
							<RichTextEditor
								value={draftHtml}
								onChange={setDraftHtml}
								placeholder="Write the announcement employees can copy..."
							/>
						</>
					) : message?.message_html ? (
						<Box
							sx={{
								"& ul, & ol": { pl: 3 },
								"& a": { color: "primary.main" },
							}}
							dangerouslySetInnerHTML={{ __html: message.message_html }}
						/>
					) : (
						<Alert severity="info">
							No active message template for this visit type yet.
							{isAdmin ? " Click “Add template” to create one." : ""}
						</Alert>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					{!editMode && (
						<Button startIcon={<ContentCopyIcon />} onClick={handleCopy}>
							Copy message
						</Button>
					)}
					<Box sx={{ flex: 1 }} />
					{isAdmin && editMode ? (
						<>
							<Button onClick={() => setEditMode(false)} disabled={saving}>
								Cancel
							</Button>
							<Button variant="contained" onClick={handleSave} disabled={saving}>
								{saving ? "Saving..." : "Save template"}
							</Button>
						</>
					) : (
						<Button onClick={onClose}>Close</Button>
					)}
				</DialogActions>
			</Dialog>

			<Snackbar
				open={Boolean(copyNotice)}
				autoHideDuration={2500}
				onClose={() => setCopyNotice("")}
				message={copyNotice}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			/>
		</>
	);
};

export default VisitTypeMessageModal;
